import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceCalculationResult, SellPriceConfig } from '../../models/sol-estimate-table/SellPriceConfig';
import { EstimateGrossMargin, EstimateSellPrice, SQWebProject } from '../../models/dtos';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { DynamicFeeCostInputs } from '../../models/sol-estimate-table/DynamicFeeCostInputs';
import { CommonDataService } from '../common-data.service';
import { roundHalfEven9 } from '../../../types/number-extensions';
import {
  EstimateUiModel,
  EstimateRowExtendedModel,
} from '../../models/sol-estimate-table/ui-models/index';
import { EstimateLastModifiedSellPriceService } from './estimate-last-modified-sell-price.service';

/**
 * Handles all misc/risk/travel-costs-related logic for the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateMiscRiskTravelService {

  constructor(
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly commonDataService: CommonDataService,
    private readonly lastModifiedSellPriceService: EstimateLastModifiedSellPriceService
  ) { }

  // ── Patching (initial load) ────────────────────────────────────

  patchMiscRiskTravelCosts(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost: number,
    sqWebProject?: SQWebProject
  ): void {
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const gm: EstimateGrossMargin | undefined = otherMetaData?.grossMargin;
    if (gm == null) {
      return;
    }
    // Stored sell prices only matter in Line Item mode; Fixed modes derive them from cost and margin.
    const sp: EstimateSellPrice | undefined = config.isFixedPriceSelected === null ? otherMetaData?.sellPrice : undefined;
    const miscRows = model.miscRiskTravelCosts.rows;
    this.patchNamedRow(miscRows, 'MISCELLANEOUS_COSTS', sqWebProject.miscellaneousCategory.sumUpCost, gm.miscellaneousCostGMPercent, sp?.miscSellPrice, config.isMarkUpSelected);
    this.patchNamedRow(miscRows, 'MECHANICAL_FEE', sqWebProject.mechanicalFeeCategory.sumUpCost, gm.mechFeeCostGMPercent, sp?.mechFeeCostSellPrice, config.isMarkUpSelected);
    this.patchNamedRow(miscRows, 'PRODUCTIVITY_RESERVE', sqWebProject.productivityReserveCategory.sumUpCost, gm.productivityReserveGMPercent, sp?.productivityReserveSellPrice, config.isMarkUpSelected);
    this.patchNamedRow(miscRows, 'SUBCONTRACTOR_FEE', sqWebProject.subcontractorFeeCategory.sumUpCost, gm.subcontractorFeeCostGMPercent, sp?.subcontractorFeeCostSellPrice, config.isMarkUpSelected);
    this.patchNamedRow(miscRows, 'TRAVEL_EXPENSE_COSTS', sqWebProject.travelExpensesCategory.sumUpCost, gm.travelExpenseGMPercent, sp?.travelSellPrice, config.isMarkUpSelected);
    const riskCosts = model.miscRiskTravelCosts.riskCosts;
    if (riskCosts) {
      const riskRows = riskCosts.rows;
      this.patchNamedRow(riskRows, 'CALCULATED_RISK_COSTS', sqWebProject.autoRiskCategory.sumUpCost, gm.autoRiskCostGMPercent, sp?.autoRiskCostSellPrice, config.isMarkUpSelected);
      this.patchNamedRow(riskRows, 'PROJECT_ENTERED_RISK_COSTS', sqWebProject.riskCategory.sumUpCost, gm.riskCostGMPercent, sp?.riskCostSellPrice, config.isMarkUpSelected);
    }

    this.recalculateMiscRiskTravelSellPrices(model, config, subTotalsCost);
  }

  private patchNamedRow(
    rows: EstimateRowExtendedModel[],
    name: string,
    cost: number,
    gmPercent: number | undefined,
    sellPrice: number | undefined,
    isMarkUp: boolean
  ): void {
    const row = rows.find(r => r.name === name);
    if (!row) return;

    row.cost = cost;
    if (gmPercent != null) {
      // Stored percent is in the active unit (MU% when markup is on); rows hold true GM%.
      row.grossMarginPercentage = isMarkUp ? this.estimateCommonFunctions.getGrossMarginPercent(gmPercent) : gmPercent;
    }
    if (sellPrice != null) {
      row.sellPrice = sellPrice;
    }
  }

  // ── Recalculation ──────────────────────────────────────────────

  /**
   * Recalculates sell prices for all misc/risk/travel rows WITHOUT overwriting
   * dynamically computed costs (SUBCONTRACTOR_FEE, CALCULATED_RISK_COSTS, MECHANICAL_FEE).
   * Use this after a manual risk dialog update to preserve the user-entered value.
   */
  recalculateMiscRiskTravelSellPricesOnly(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost?: number): void {
    const stc = subTotalsCost ?? model.subTotals.cost;

    const miscResult = this.calculateAndUpdateRowGroup(model.miscRiskTravelCosts.rows, config, stc);

    let totalCost = miscResult.totalCost;
    let totalSellPrice = miscResult.totalSellPrice;

    const riskCosts = model.miscRiskTravelCosts.riskCosts;
    if (riskCosts) {
      const riskResult = this.calculateAndUpdateRowGroup(riskCosts.rows, config, stc);

      totalCost += riskResult.totalCost;
      totalSellPrice += riskResult.totalSellPrice;

      this.estimateCommonFunctions.updateSectionTotalsHeader(riskCosts.headerRow, riskResult.totalCost, riskResult.totalSellPrice, stc, config);
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.miscRiskTravelCosts.headerRow,
      totalCost, totalSellPrice, stc, config
    );

    this.updateNonTaxableItemSellPrices(model);
  }

  recalculateMiscRiskTravelSellPrices(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost?: number): void {
    const stc = subTotalsCost ?? model.subTotals.cost;

    // Recalculate dynamic costs before computing sell prices
    this.recalculateDynamicRowCosts(model);

    const miscResult = this.calculateAndUpdateRowGroup(model.miscRiskTravelCosts.rows, config, stc);

    let totalCost = miscResult.totalCost;
    let totalSellPrice = miscResult.totalSellPrice;

    const riskCosts = model.miscRiskTravelCosts.riskCosts;
    if (riskCosts) {
      const riskResult = this.calculateAndUpdateRowGroup(riskCosts.rows, config, stc);

      totalCost += riskResult.totalCost;
      totalSellPrice += riskResult.totalSellPrice;

      this.estimateCommonFunctions.updateSectionTotalsHeader(riskCosts.headerRow, riskResult.totalCost, riskResult.totalSellPrice, stc, config);
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.miscRiskTravelCosts.headerRow,
      totalCost, totalSellPrice, stc, config
    );

    this.updateNonTaxableItemSellPrices(model);
  }

  // ── Non-Taxable Items ──────────────────────────────────────────

  /**
   * Distributes the MISCELLANEOUS_COSTS row sell price across the non-taxable items the
   * cost calculation collected, so sales tax can exclude them.
   *
   * Allocation is pro-rata on cost, which matches the GM%/MU% formulas (both are linear in cost)
   * and also stays correct when the user types a sell price directly in Line Item mode.
   */
  private updateNonTaxableItemSellPrices(model: EstimateUiModel): void {
    const nonTaxableItems = this.estimateCommonFunctions.estimateNonTaxableItemsDictionary;
    if (nonTaxableItems.size === CONSTANTS.ZERO) {
      return;
    }

    const miscRow = this.findNamedRow(model, 'MISCELLANEOUS_COSTS');
    const miscCost = miscRow?.cost ?? CONSTANTS.ZERO;

    if (!miscRow || miscCost === CONSTANTS.ZERO) {
      nonTaxableItems.forEach(item => { item.sellPrice = CONSTANTS.ZERO; });
      return;
    }

    nonTaxableItems.forEach(item => {
      item.sellPrice = roundHalfEven9(miscRow.sellPrice * (item.cost / miscCost));
    });
  }

  // ── Dynamic Row Cost Recalculation ─────────────────────────────

  /**
   * Recalculates the cost for dynamically computed rows:
   *  - SUBCONTRACTOR_FEE
   *  - CALCULATED_RISK_COSTS
   *  - MECHANICAL_FEE
   *
   * These rows derive their cost from other sections of the estimate model
   * using shared formulas in EstimateCommonFunctionsService.
   * The order matters: subcontractor fee → auto risk → mechanical fee,
   * because each subsequent formula depends on the previous result.
   */
  private recalculateDynamicRowCosts(model: EstimateUiModel): void {
    const estimateSettings = this.commonDataService.getEstimateMetaData()?.estimateSettings;
    const feesData = this.commonDataService.getFeesData();

    if (!estimateSettings || !feesData) return;

    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    // Helper to get cost of a named row
    const getNamedRowCost = (name: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, name);
      return row ? row.cost : CONSTANTS.ZERO;
    };

    // ── 1. Subcontractor Fee ─────────────────────────────────────
    const subcontractorTotalCost = model.subContractorCosts.headerRow.cost;
    const subFeeRow = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, 'SUBCONTRACTOR_FEE');
    if (subFeeRow) {
      subFeeRow.cost = this.estimateCommonFunctions.calculateSubcontractorFeeCost(
        subcontractorTotalCost,
        estimateSettings.useSubFee,
        feesData.subcontractorFeePercentage,
        feesData.subcontractorFeeThresholdCost
      );
    }

    // Build shared cost inputs for auto risk and mechanical fee.
    // Include Use Tax / HST / PST cost so auto risk accounts for it.
    const costInputs: DynamicFeeCostInputs = {
      materialCost: model.materialCosts.materialCostsHeader.cost,
      laborCost: model.laborCosts.laborCostsHeader.cost,
      miscCost: getNamedRowCost('MISCELLANEOUS_COSTS'),
      subcontractorCost: subcontractorTotalCost,
      travelCost: getNamedRowCost('TRAVEL_EXPENSE_COSTS'),
      subcontractorFeeCost: subFeeRow?.cost ?? CONSTANTS.ZERO,
      productivityReserveCost: getNamedRowCost('PRODUCTIVITY_RESERVE'),
      taxCost: model.taxMaterialRate.headerRow.cost,
    };

    // ── 2. Calculated Risk (Auto Risk) ───────────────────────────
    model.miscRiskTravelCosts.tdcForRisk = this.estimateCommonFunctions.calculateTdcForRisk(
      costInputs,
      estimateSettings.useAutoRisk
    );
    const autoRiskRow = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, 'CALCULATED_RISK_COSTS');
    if (autoRiskRow) {
      autoRiskRow.cost = this.estimateCommonFunctions.calculateAutoRiskCost(
        costInputs,
        estimateSettings.useAutoRisk,
        estimateSettings.autoRiskPercentage
      );
    }

    // ── 3. Mechanical Fee ────────────────────────────────────────
    const mechFeeRow = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, 'MECHANICAL_FEE');
    if (mechFeeRow) {
      mechFeeRow.cost = this.estimateCommonFunctions.calculateMechanicalFeeCost(
        costInputs,
        autoRiskRow?.cost ?? CONSTANTS.ZERO,
        estimateSettings.useMechFee,
        feesData.mechanicalFeePercentage
      );
    }
  }

  private calculateAndUpdateRowGroup(
    rows: EstimateRowExtendedModel[],
    config: SellPriceConfig,
    stc: number
  ): { totalCost: number; totalSellPrice: number } {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const row of rows) {
      const result = this.calculateAndUpdateRow(row, config, stc);
      totalCost += result.cost;
      totalSellPrice += result.sellPrice;
    }

    return { totalCost, totalSellPrice };
  }

  private calculateAndUpdateRow(
    row: EstimateRowExtendedModel,
    config: SellPriceConfig,
    stc: number
  ): { cost: number; sellPrice: number } {
    const result = config.isFixedPriceSelected !== null
      ? this.computeFixedPriceResult(row, config)
      : this.computeLineItemResult(row, config);
    this.estimateCommonFunctions.updateRowResult(row, result, stc);
    return { cost: row.cost, sellPrice: result.sellPrice };
  }

  private computeFixedPriceResult(
    row: EstimateRowExtendedModel,
    config: SellPriceConfig
  ): SellPriceCalculationResult {
    const presentPercent = row.grossMarginPercentage;
    const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    const preservedSellPrice = this.estimateCommonFunctions.resolvePreservedSellPrice(
      row, config, this.lastModifiedSellPriceService.isNamedRowSellPriceLastModified(row.name)
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(
      row.cost, gmPercent, config.isMarkUpSelected, preservedSellPrice
    );
  }

  private computeLineItemResult(
    row: EstimateRowExtendedModel,
    config: SellPriceConfig
  ): SellPriceCalculationResult {
    if (this.lastModifiedSellPriceService.isNamedRowSellPriceLastModified(row.name)) {
      return this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, row.sellPrice);
    }
    // Row GM% is always true GM% after a calculation; convert to MU% when markup is on.
    const presentPercent = row.grossMarginPercentage;
    const percent = this.estimateCommonFunctions.resolveRowGMPercent(
      config, Number.isNaN(presentPercent) ? undefined : presentPercent
    );
    return this.estimateCommonFunctions.calculateRowSellPrice(row.cost, percent, config.isMarkUpSelected);
  }

  // ── Totals Recalculation ───────────────────────────────────────

  recalculateMiscRiskTravelTotals(model: EstimateUiModel, config: SellPriceConfig): void {
    const miscResult = this.calculateTotalsForRowGroup(model.miscRiskTravelCosts.rows);

    let totalCost = miscResult.totalCost;
    let totalSellPrice = miscResult.totalSellPrice;

    const riskCosts = model.miscRiskTravelCosts.riskCosts;
    if (riskCosts) {
      const riskResult = this.calculateTotalsForRowGroup(riskCosts.rows);

      totalCost += riskResult.totalCost;
      totalSellPrice += riskResult.totalSellPrice;

      this.estimateCommonFunctions.updateSectionTotalsHeader(
        riskCosts.headerRow,
        riskResult.totalCost, riskResult.totalSellPrice,
        model.subTotals.cost, config
      );
    }

    this.estimateCommonFunctions.updateSectionTotalsHeader(
      model.miscRiskTravelCosts.headerRow,
      totalCost, totalSellPrice, model.subTotals.cost, config
    );

    this.updateNonTaxableItemSellPrices(model);
  }

  private calculateTotalsForRowGroup(rows: EstimateRowExtendedModel[]): { totalCost: number; totalSellPrice: number } {
    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (const row of rows) {
      totalCost += row.cost;
      totalSellPrice += row.sellPrice;
    }

    return { totalCost, totalSellPrice };
  }

  // ── Reactive Handler ───────────────────────────────────────────

  onMiscRiskTravelRowPercentChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    rowName: string,
    newPercent: number,
    isGMPercent: boolean
  ): void {
    const isMarkUp = !isGMPercent;
    const row = this.findNamedRow(model, rowName);
    if (!row) return;

    const cost = row.cost;
    const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, newPercent, isMarkUp);

    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateMiscRiskTravelTotals(model, config);
  }

  /**
   * Called when user edits a misc/risk/travel row's sell price directly (Line Item mode only).
   * Derives GM%, MU%, and TDC% from the new sell price and row cost, then
   * recalculates misc/risk/travel section totals.
   */
  onMiscRiskTravelRowSellPriceChanged(
    model: EstimateUiModel,
    config: SellPriceConfig,
    rowName: string,
    newSellPrice: number
  ): void {
    const row = this.findNamedRow(model, rowName);
    if (!row) {
      return;
    }

    const result = this.estimateCommonFunctions.recomputePercentsFromSellPrice(row.cost, newSellPrice);
    this.estimateCommonFunctions.updateRowResult(row, result, model.subTotals.cost);
    this.recalculateMiscRiskTravelTotals(model, config);
  }

  // ── Refresh Displayed Percentages (GM↔MU toggle) ──────────────

  refreshMiscRiskTravelPercentages(model: EstimateUiModel): void {
    for (const row of model.miscRiskTravelCosts.rows) {
      this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(row);
    }
    this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(model.miscRiskTravelCosts.headerRow);

    const riskCosts = model.miscRiskTravelCosts.riskCosts;
    if (riskCosts) {
      for (const row of riskCosts.rows) {
        this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(row);
      }
      this.estimateCommonFunctions.refreshRowPercentsFromSellPrice(riskCosts.headerRow);
    }
  }

  // ── Named Row Lookup ───────────────────────────────────────────

  /** Finds a named row in misc/travel or risk arrays. */
  findNamedRow(
    model: EstimateUiModel,
    rowName: string
  ): EstimateRowExtendedModel | null {
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;
    return this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, rowName);
  }

}
